import { Link } from 'react-router-dom';

export default function Guide() {
  return (
    <article className="guide">
      <h1>How to copy</h1>
      <p className="lead">
        The rules are short, and they matter: every copy is scored character by character against a decoder, so the
        copy has to say exactly what went out over the air, and nothing else.
      </p>

      <h2>What to write</h2>
      <table className="conventions">
        <thead>
          <tr>
            <th>You hear</th>
            <th>You write</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Letters and numbers</td>
            <td>
              <code>A-Z 0-9</code>, in any case; they're capitalised for you
            </td>
          </tr>
          <tr>
            <td>Punctuation</td>
            <td>
              <code>. , ? /</code>
            </td>
          </tr>
          <tr>
            <td>BT, the break</td>
            <td>
              <code>=</code>
            </td>
          </tr>
          <tr>
            <td>A prosign</td>
            <td>
              its letters in angle brackets: <code>&lt;AR&gt; &lt;SK&gt; &lt;KN&gt; &lt;BK&gt; &lt;AS&gt;</code>
            </td>
          </tr>
          <tr>
            <td>The error sign, eight dits</td>
            <td>
              <code>&lt;HH&gt;</code>
            </td>
          </tr>
          <tr>
            <td>One character you're unsure of</td>
            <td>
              your best guess in square brackets: <code>W7[K]Z</code>
            </td>
          </tr>
          <tr>
            <td>A stretch you couldn't copy</td>
            <td>
              a single <code>#</code>, however long it was
            </td>
          </tr>
        </tbody>
      </table>
      <p className="muted small">
        Spaces separate words. A line break counts as a space, so start each over on its own line if that helps you
        keep your place.
      </p>

      <h2>The rules</h2>
      <ul className="rules">
        <li>
          <strong>Every repeat, as sent.</strong> <code>CQ CQ CQ DE W1ABC W1ABC K</code> is written out in full, never
          as "CQ x3".
        </li>
        <li>
          <strong>What was sent, not what was meant.</strong> If the operator fumbles, copy the fumble:{' '}
          <code>NAMR &lt;HH&gt; NAME</code>.
        </li>
        <li>
          <strong>Both sides of a QSO, in order.</strong> The two stations are usually within a few tens of hertz of
          each other, so both are in the clip.
        </li>
        <li>
          <strong>Only the centre signal.</strong> A station at a clearly different pitch is another clip's business.
        </li>
        <li>
          <strong>Cut-off characters at either end</strong> of a clip are a <code>#</code>.
        </li>
      </ul>

      <h2>Keep it blind</h2>
      <p>
        Two copies that agree are only strong evidence if they were made independently. So:
      </p>
      <ul className="rules">
        <li>
          <strong>Don't look anything up.</strong> Not on QRZ, the cluster or a spotting network. If you didn't hear
          it, put your best guess in brackets, <code>W7[K]Z</code>, or a <code>#</code> if you have none. A looked-up
          callsign is a guess that looks like a copy.
        </li>
        <li>
          <strong>Don't compare copies</strong> with another copier, even after saving: a copy can still be revised,
          and one revised after a comparison is no longer independent. This site never shows you another copier's
          work, and never shows yours to another copier; only WB5SKM sees the copies.
        </li>
        <li>
          <strong>If a decoder helped, say so.</strong> Decoder-assisted copies are welcome. They're filed separately,
          because a decoder is what's being tested.
        </li>
        <li>
          <strong>Some clips have no Morse in them.</strong> That's a finding, not a failure. Tick{' '}
          <em>There's no CW in this clip</em>.
        </li>
      </ul>

      <h2>Keys</h2>
      <table className="keys">
        <tbody>
          <tr>
            <td>
              <kbd>Esc</kbd>
            </td>
            <td>play / pause, even while you type</td>
          </tr>
          <tr>
            <td>
              <kbd>F1</kbd> <kbd>F2</kbd>
            </td>
            <td>back / forward 3 seconds</td>
          </tr>
          <tr>
            <td>
              <kbd>F3</kbd> <kbd>F4</kbd>
            </td>
            <td>slower / faster. The pitch stays put.</td>
          </tr>
          <tr>
            <td>
              <kbd>F6</kbd>
            </td>
            <td>loop on / off. Drag across the strip to set a loop, or use A and B.</td>
          </tr>
          <tr>
            <td>
              <kbd>Ctrl</kbd>+<kbd>Enter</kbd>
            </td>
            <td>save your copy</td>
          </tr>
          <tr>
            <td>
              <kbd>Space</kbd> <kbd>&larr;</kbd> <kbd>&rarr;</kbd>
            </td>
            <td>play / pause and skip, when you're not typing</td>
          </tr>
        </tbody>
      </table>

      <h2>What's kept</h2>
      <p>
        Your callsign and name from your invite, your copies, when you saved them, and how much of each clip you played.
        No email address, no tracking, no ads. Your callsign is listed on the home page only if you choose to be listed{' '}
        <Link to="/join">on your invite page</Link>. Accepted copies are credited to your callsign in the skimmer's
        test records.
      </p>

      <h2>Is your QSO in here?</h2>
      <p>
        Every clip is amateur radio as it went out over the air. If you hear one of your own contacts and would rather
        it weren't here, contact WB5SKM (the address is on QRZ.com) and it comes down.
      </p>
    </article>
  );
}
